import { useId, useState, type CSSProperties } from 'react'
import { Box, Button, Card, Checkbox, Group, Text } from '@grovus/design-system'
import { accountDetail, type AccountId, type ClientAccount } from '../data/account'
import type { Client } from '../data/clients'
import { MEMBERS_COPY, type Dict, type Lang } from '../i18n'
import { can } from '../lib/roles'
import { fmt } from '../lib/format'
import { metaGuideUrl } from '../lib/guideUrl'
import { PanelHeading } from './PanelHeading'
import type { AccountConnections, ConnectionNotice } from './useAccountConnections'
import type { PanelProps } from './useDisclosurePanel'

interface Props {
  t: Dict
  /** The active client. Its accounts are the Meta section; no other client's are listed. */
  client: Client
  accs: Record<AccountId, boolean>
  toggleAcc: (id: AccountId) => void
  panelProps: PanelProps
  /** The connection surface. Absent, the panel lists accounts only, as it did before GRO-41. */
  connections?: AccountConnections
  /** When "now" is, for the days-left count. A prop so a test can fix it. */
  now?: Date
  lang?: Lang
}

const DAY_MS = 86_400_000

type Tone = 'muted' | 'warn' | 'bad'

/**
 * The one line under an account naming its connection state (GRO-41 design, section 7).
 * The four broken states use the `bad` ramp and the expiring one the `warn` ramp, and
 * every one of them says what it is in words, so no state is carried by colour alone.
 */
function stateLine(t: Dict, account: ClientAccount, now: Date): { text: string; tone: Tone } | null {
  const connection = account.connection
  if (connection === undefined || connection.state === 'demo') return null
  switch (connection.state) {
    case 'connected':
      return {
        text: connection.lastSuccessfulPullAt === null ? t.accStateWaiting : t.accStateConnected,
        tone: 'muted',
      }
    case 'credential_expiring': {
      const expires = connection.credentialExpiresAt === null ? NaN : Date.parse(connection.credentialExpiresAt)
      const days = Number.isNaN(expires) ? 0 : Math.max(0, Math.ceil((expires - now.getTime()) / DAY_MS))
      return { text: fmt(t.accStateExpiring, { n: days }), tone: 'warn' }
    }
    case 'credential_expired':
      return { text: t.accStateExpired, tone: 'bad' }
    case 'credential_revoked':
      return { text: t.accStateRevoked, tone: 'bad' }
    case 'credential_rejected':
      return { text: t.accStateRejected, tone: 'bad' }
    case 'access_withdrawn':
      return { text: t.accStateWithdrawn, tone: 'bad' }
  }
}

function noticeText(t: Dict, notice: ConnectionNotice): string | null {
  if (notice === 'notAvailable') return t.accNotAvailable
  if (notice === 'alreadyRemoved') return t.accAlreadyRemoved
  if (notice === 'unreachable') return t.accUnreachable
  if (notice === 'refreshFailed') return t.accRefreshFailed
  return null
}

const ROW: CSSProperties = { borderTop: '1px solid var(--mantine-color-default-border)' }

/**
 * The ad-accounts panel, scoped to one client, and since GRO-41 its connection surface.
 *
 * GRO-19 hid its trigger for an empty company because the panel read the fixture's three
 * accounts whatever the company was, and so would have contradicted the empty state. With
 * the panel scoped to the client that reason is gone: a client with nothing connected
 * renders the same "Not connected yet" line under Meta as it does under TikTok and Google
 * (design: docs/plans/2026-09-18-gro-20-client-account-panels-design.md).
 *
 * The connection additions are built from the spec rather than drawn first (decided by
 * Max at GRO-41's design checkpoint): a state line under each real account, the accounts
 * the partner share exposed under "Shared with Grovus", which the wireframe's first-run
 * step 3 already draws as a list to pick from, and the demo account's removal behind an
 * inline confirmation, never a browser dialog.
 */
export function AccountPanel({
  t, client, accs, toggleAcc, panelProps, connections, now = new Date(), lang = 'en',
}: Props) {
  // Members and Viewers see Connect and Remove demo account disabled, with the reason
  // under them (GRO-106, board `7-account-member`). The backend stays the gate.
  const restricted = !can(client.role, 'connectAccount')
  const [confirmingId, setConfirming] = useState<AccountId | null>(null)
  // A role that lost the right to remove also loses an open confirmation.
  const confirming = restricted ? null : confirmingId
  const [connectOpen, setConnectOpen] = useState(false)
  const connectId = useId()
  const notice = connections ? noticeText(t, connections.notice) : null
  // The demo company holds no real account (GRO-131, D7): nothing to connect there.
  const available = client.isDemo ? [] : connections?.available ?? []

  return (
    <Card
      role="group"
      aria-label={t.adAccounts}
      className="header-panel"
      style={{ right: 0, '--panel-width': '344px' } as CSSProperties}
      {...panelProps}
    >
      <PanelHeading>{t.platMeta}</PanelHeading>
      {client.isDemo && (
        <Text fz="xs" pb="xs" className="grv-muted" data-testid="demo-company-hint">{t.accDemoCompanyHint}</Text>
      )}
      {client.accounts.length === 0 && available.length === 0 && (
        <Text fz="sm" className="grv-muted">{t.notConnectedYet}</Text>
      )}
      {client.accounts.map((a) => {
        const line = stateLine(t, a, now)
        const detail = accountDetail(a)
        const removable = a.isDemo && connections !== undefined && connections.canRemoveDemo
        return (
          <Box key={a.id} py="sm" style={ROW}>
            <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
              <Checkbox
                checked={accs[a.id] !== false}
                onChange={() => toggleAcc(a.id)}
                label={(
                  <Box>
                    {/* The marker sits in the label, so it is part of the checkbox's
                        accessible name and not only something sighted users get. */}
                    <Text fw={600} fz="sm">{a.isDemo ? t.demoMarkLong : a.name}</Text>
                    {detail && (
                      <Text fz="xs" className="grv-muted" data-testid="account-detail">{detail}</Text>
                    )}
                    {line && (
                      <Text fz="xs" className={`grv-${line.tone}`} data-testid="account-state">
                        {line.text}
                      </Text>
                    )}
                  </Box>
                )}
              />
              {removable && confirming !== a.id && (
                <Button
                  variant="subtle"
                  size="compact-sm"
                  onClick={() => setConfirming(a.id)}
                  disabled={restricted || connections.pending !== null}
                >
                  {t.accRemove}
                </Button>
              )}
            </Group>
            {removable && confirming === a.id && (
              <Box mt="xs" role="group" aria-label={t.accRemoveConfirm}>
                <Text fz="xs">{t.accRemoveConfirm}</Text>
                <Group gap="xs" mt={6}>
                  <Button
                    size="compact-sm"
                    color="bad"
                    loading={connections.pending === a.id}
                    disabled={connections.pending !== null}
                    onClick={() => {
                      setConfirming(null)
                      connections.removeDemo(a.id)
                    }}
                  >
                    {t.accRemove}
                  </Button>
                  <Button size="compact-sm" variant="default" onClick={() => setConfirming(null)}>
                    {t.accRemoveKeep}
                  </Button>
                </Group>
              </Box>
            )}
          </Box>
        )
      })}

      {connections && available.length > 0 && (
        <Box pt="sm" mt="sm" style={ROW} data-testid="shared-with-grovus">
          <PanelHeading>{t.accShared}</PanelHeading>
          {available.map((account) => (
            <Group key={account.id} justify="space-between" wrap="nowrap" py={6} gap="sm">
              <Box>
                <Text fw={600} fz="sm">{account.name}</Text>
                <Text fz="xs" className="grv-muted">
                  {accountDetail(account)}
                </Text>
              </Box>
              <Button
                size="compact-sm"
                variant="default"
                loading={connections.pending === account.id}
                disabled={restricted || connections.pending !== null}
                onClick={() => connections.connect(account.id)}
              >
                {t.accConnect}
              </Button>
            </Group>
          ))}
        </Box>
      )}

      {connections?.demoPrompt && (
        <Box mt="sm" pt="sm" style={ROW} role="group" aria-label={t.accDemoPrompt} data-testid="demo-prompt">
          <Text fz="sm">{t.accDemoPrompt}</Text>
          <Group gap="xs" mt={6}>
            <Button size="compact-sm" color="bad" onClick={() => connections.answerDemoPrompt('delete')}>
              {t.accDemoPromptDelete}
            </Button>
            <Button size="compact-sm" variant="default" onClick={() => connections.answerDemoPrompt('keep')}>
              {t.accDemoPromptKeep}
            </Button>
          </Group>
        </Box>
      )}

      {restricted && connections && (available.length > 0 || (connections.canRemoveDemo && client.accounts.some((a) => a.isDemo))) && (
        <Text fz="xs" pt="xs" className="grv-muted" data-testid="connect-why">{MEMBERS_COPY[lang].connectWhy}</Text>
      )}

      {connections?.availableFailed && !client.isDemo && (
        <Group mt="xs" gap="xs" wrap="nowrap" align="center" justify="space-between">
          <Text fz="xs" className="grv-warn" data-testid="shared-failed">{t.accSharedFailed}</Text>
          <Button
            size="compact-sm"
            variant="default"
            disabled={connections.pending !== null}
            onClick={() => connections.reloadAvailable()}
          >
            {t.retry}
          </Button>
        </Group>
      )}

      {notice && (
        <Group mt="xs" gap="xs" wrap="nowrap" align="center" justify="space-between">
          <Text fz="xs" role="status" className="grv-warn">{notice}</Text>
          {connections?.notice === 'refreshFailed' && (
            <Button
              size="compact-sm"
              variant="default"
              loading={connections.pending === 'retry'}
              disabled={connections.pending !== null}
              onClick={() => connections.retry()}
            >
              {t.retry}
            </Button>
          )}
        </Group>
      )}

      <Box pt="sm" mt="sm" style={ROW}>
        <PanelHeading>{t.platTikTok}</PanelHeading>
        <Text fz="sm" className="grv-muted">{t.notConnectedYet}</Text>
      </Box>
      <Box pt="sm" mt="sm" style={ROW}>
        <PanelHeading>{t.platGoogle}</PanelHeading>
        <Text fz="sm" className="grv-muted">{t.notConnectedYet}</Text>
      </Box>

      {/* The customer's one step of the connection (GRO-66): stage 1 of the connection guide,
          inside the panel rather than a nested overlay, so Escape still closes the panel.
          The steps themselves live on the guide page (GRO-98), which opens in a new tab so
          the panel and the app stay where they were. */}
      {!client.isDemo && <Box pt="sm" mt="sm" style={ROW}>
        <Button
          variant="default"
          size="xs"
          aria-expanded={connectOpen}
          aria-controls={connectId}
          onClick={() => setConnectOpen((open) => !open)}
          data-testid="connect-platform-toggle"
        >
          {t.accConnectPlatform}
        </Button>
        <Box id={connectId} hidden={!connectOpen} pt="xs">
          <Text fz="xs" style={{ lineHeight: 1.5 }} data-testid="connect-need">{t.accConnectNeed}</Text>
          <Text fz="xs" pt={6} className="grv-muted" style={{ lineHeight: 1.5 }} data-testid="connect-then">
            {fmt(t.accConnectThen, { shared: t.accShared })}
          </Text>
          <Text fz="xs" pt={6}>
            <a href={metaGuideUrl(lang)} target="_blank" rel="noopener" data-testid="connect-guide">{t.accConnectGuide}</a>
          </Text>
        </Box>
      </Box>}

      <Box pt="sm" mt="sm" style={{ borderTop: '2px solid var(--mantine-color-default-border)' }}>
        <Text fz={11} className="grv-muted" style={{ lineHeight: 1.5 }}>{t.mergeNote}</Text>
      </Box>
    </Card>
  )
}
